import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface AssignChapterModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookId: string;
  bookTitle: string;
  chapterId: string;
  chapterTitle: string;
}

export default function AssignChapterModal({
  isOpen,
  onClose,
  bookId,
  bookTitle,
  chapterId,
  chapterTitle
}: AssignChapterModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applications, setApplications] = useState<any[]>([]);
  const [selectedApplication, setSelectedApplication] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [matchingBookIdea, setMatchingBookIdea] = useState<{ id: string } | null>(null);

  useEffect(() => {
    if (isOpen) {
      findMatchingBookIdea();
    }
  }, [isOpen, bookTitle]);

  const findMatchingBookIdea = async () => {
    try {
      setLoading(true);
      setError(null);

      // Find a book idea with a matching title
      const { data: bookIdeas, error: bookError } = await supabase
        .from('book_ideas')
        .select('id')
        .ilike('title', bookTitle)
        .limit(1);

      if (bookError) throw bookError;

      if (!bookIdeas?.length) {
        setError(`No book idea found matching "${bookTitle}". Please create a book idea with this title first.`);
        return;
      }

      setMatchingBookIdea(bookIdeas[0]);
      await loadApplications(bookIdeas[0].id);
    } catch (err) {
      console.error('Error finding matching book idea:', err);
      setError('Failed to find matching book idea');
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async (bookIdeaId: string) => {
    try {
      const { data, error: fetchError } = await supabase
        .from('beta_reader_applications')
        .select(`
          id,
          reader_id,
          status,
          profiles:reader_id(
            username,
            full_name
          )
        `)
        .eq('idea_id', bookIdeaId)
        .eq('status', 'approved');

      if (fetchError) throw fetchError;
      setApplications(data || []);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError('Failed to load beta reader applications');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedApplication || !matchingBookIdea) return;

    try {
      setLoading(true);
      setError(null);

      const application = applications.find(a => a.id === selectedApplication);
      if (!application) {
        throw new Error('Selected application not found');
      }

      const { error: createError } = await supabase
        .from('beta_reader_assignments')
        .insert({
          application_id: application.id,
          reader_id: application.reader_id,
          idea_id: matchingBookIdea.id,
          chapter_id: chapterId,
          title: `Review Chapter: ${chapterTitle}`,
          description: `Please review chapter "${chapterTitle}" from "${bookTitle}" and provide your feedback.`,
          status: 'pending',
          due_date: dueDate || null
        });

      if (createError) throw createError;
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create assignment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="border-b px-6 py-4">
            <h2 className="text-lg font-semibold">Assign Chapter to Beta Reader</h2>
          </div>

          <div className="p-6 space-y-6">
            {error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-red-700">{error}</p>
                    {error.includes('No book idea found') && (
                      <p className="text-sm text-red-600 mt-2">
                        To assign chapters to beta readers:
                        <ol className="list-decimal ml-4 mt-1">
                          <li>Create a book idea with the same title as this book</li>
                          <li>Enable beta reader applications for the book idea</li>
                          <li>Approve some beta reader applications</li>
                        </ol>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : loading ? (
              <div className="text-center py-4">
                <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-gray-600 mt-2">Loading...</p>
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-600">No approved beta readers available.</p>
                <p className="text-sm text-gray-500 mt-2">
                  Approve some beta reader applications first.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Beta Reader
                  </label>
                  <select
                    value={selectedApplication}
                    onChange={(e) => setSelectedApplication(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a beta reader</option>
                    {applications.map((app) => (
                      <option key={app.id} value={app.id}>
                        {app.profiles.full_name || app.profiles.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                <div className="flex justify-end gap-4">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !selectedApplication}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading ? 'Assigning...' : 'Assign Chapter'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}